<template>
	<div @keydown.esc="$emit('close-ai')" tabindex='0'>
		<transition name="translate-left">
			<div class='a-ai-chat' v-if='value'>
				<div class='a-ai-chat-header'>
					<span class='a-ai-chat-header-avatar'>
						<v-icon icon='ai' size='22' />
					</span>
					<span class='a-ai-chat-header-info'>
						<span class='a-ai-chat-header-title'>{{$t('account.home.ai.full_title')}}</span>
						<span class='a-ai-chat-header-status'><i></i>{{$t('account.home.ai.online')}}</span>
					</span>
					<div class='a-ai-chat-header-close' :title="$t('account.home.ai.new_dialog')" @click='newDialog'>
						<v-icon icon='change' size='17' />
					</div>
					<div class='a-ai-chat-header-close' @click="$emit('close-ai')">
						<v-icon icon='cross' size='17' />
					</div>
				</div>

				<div class='a-ai-chat-body' ref='body'>
					<div class='a-ai-chat-row' :class='{"is-ai": m.ai}' v-for='(m, i) in messages' :key='i'>
						<span class='a-ai-chat-avatar' v-if='m.ai'>
							<v-icon icon='ai' size='16' />
						</span>
						<span class='a-ai-chat-bubble' :class='{"is-error": m.error}'>
							<span class='a-ai-chat-bubble-text'>{{m.text}}</span>
							<template v-if='m.recommendation && m.recommendation.hotels && m.recommendation.hotels.length'>
								<span class='a-ai-chat-card'
									:class='{"is-loading": m.searching}'
									v-for='hotel in m.recommendation.hotels' :key='hotel.code'
									@click='openHotel(m, hotel)'
								>
									<span class='a-ai-chat-card-info'>
										<span class='a-ai-chat-card-name'>{{hotel.name}}</span>
										<span class='a-ai-chat-card-meta'>{{hotel.destination_name}}</span>
										<span class='a-ai-chat-card-reason'>{{hotel.reason}}</span>
									</span>
								</span>
							</template>
							<span class='a-ai-chat-time'>{{m.time}}</span>
						</span>
					</div>

					<div class='a-ai-chat-row is-ai' v-if='typing'>
						<span class='a-ai-chat-avatar'>
							<v-icon icon='ai' size='16' />
						</span>
						<span class='a-ai-chat-bubble a-ai-chat-typing'><i></i><i></i><i></i></span>
					</div>
				</div>

				<div class='a-ai-chat-footer'>
					<input class='a-ai-chat-input'
						v-model='draft'
						maxlength='2000'
						:disabled='blocked'
						:placeholder="$t('account.home.ai.placeholder')"
						@keydown.enter='send(draft)'
					/>
					<span class='a-ai-chat-send' :class='{"is-disabled": blocked || typing}' @click='send(draft)'>
						<v-icon icon='arrow-right' size='18' />
					</span>
				</div>
			</div>
		</transition>
		<transition name='fade'>
			<div class="backdrop" v-if='value' @click="$emit('close-ai')"></div>
		</transition>
	</div>
</template>

<script>
	import { disablePageScroll, enablePageScroll } from 'scroll-lock';
	import { assistant, hotelSearch, getAutocompleteDestinationsOrHotels, getAutocompleteNationality } from '@/services/request';

	const STORAGE_KEY = 'ai_conversation_id';

	export default {
		props: {
			value: {
				type: Boolean,
				default: false
			}
		},
		data: () => ({
			messages: [],
			conversationId: localStorage.getItem(STORAGE_KEY) || null,
			draft: '',
			typing: false,
			blocked: false,
			loaded: false
		}),
		methods: {
			greeting() {
				this.messages = [{ ai: true, text: this.$t('account.home.ai.greeting'), time: this.$moment().format('HH:mm') }];
			},
			newDialog() {
				this.conversationId = null;
				localStorage.removeItem(STORAGE_KEY);
				this.blocked = false;
				this.greeting();
			},
			loadHistory() {
				assistant.conversation(this.conversationId).then(r => {
					this.messages = r.data.messages.map(m => ({
						ai: m.role === 'assistant',
						text: m.content,
						recommendation: m.recommendation,
						time: this.$moment(m.created_at).format('HH:mm')
					}));
					this.scrollDown();
				}).catch(() => this.newDialog());
			},
			send(text) {
				text = (text || '').trim();
				if (!text || this.typing || this.blocked) return;

				this.messages.push({ ai: false, text, time: this.$moment().format('HH:mm') });
				this.draft = '';
				this.typing = true;
				this.scrollDown();

				assistant.chat({ message: text, ...(this.conversationId ? { conversationId: this.conversationId } : {}) }).then(r => {
					this.conversationId = r.data.conversationId;
					localStorage.setItem(STORAGE_KEY, this.conversationId);

					const message = {
						ai: true,
						text: r.data.reply,
						recommendation: r.data.recommendation,
						time: this.$moment().format('HH:mm')
					};
					this.messages.push(message);

					// один поиск на всю подборку — прогреваем, чтобы клик по карточке был мгновенным
					if (message.recommendation && message.recommendation.hotels && message.recommendation.hotels.length) {
						this.ensureSearch(message).catch(() => {});
					}
				}).catch(err => {
					const message = err.response && err.response.data && err.response.data.message;
					// суточный лимит и троттлинг оба приходят как 429 — различаем по телу
					if (err.response && err.response.status === 429 && message !== 'Too Many Attempts.') {
						this.blocked = true;
					}
					this.messages.push({
						ai: true,
						error: true,
						text: message || this.$t('account.home.ai.error'),
						time: this.$moment().format('HH:mm')
					});
				}).finally(() => {
					this.typing = false;
					this.scrollDown();
				});
			},
			ensureSearch(message) {
				if (!message.searchPromise) {
					const search = message.recommendation.search;
					const destinationName = message.recommendation.hotels[0].destination_name;

					const promise = Promise.all([
						hotelSearch({ ...search, hotel: null }),
						getAutocompleteDestinationsOrHotels({ search: destinationName }),
						getAutocompleteNationality({ search: '' })
					]).then(([searchRes, destinations, nationalities]) => {
						const initialHotel = destinations.data.find(el => el.type !== 'hotel' && el.destinationCode === search.destination)
							|| destinations.data[0] || null;
						const foundNationality = nationalities.data.find(el => el.code === search.nationality);

						return {
							orderId: searchRes.data.orderId,
							storageId: searchRes.data.storageId,
							source: {
								...search,
								childrenCount: search.children.length,
								date: [Date.parse(search.dateFrom), Date.parse(search.dateTo)],
								initialHotel,
								initialNationality: foundNationality ? { code: foundNationality.code, name: foundNationality.name } : null,
								hotel: null,
								rooms: [],
								tourists: [],
								travelersIds: [],
								resetCache: false,
								showSimilar: false,
								tripName: null
							}
						};
					}).catch(err => {
						// storageId живёт в кеше и мог протухнуть — при следующем клике пробуем заново
						message.searchPromise = null;
						throw err;
					});

					this.$set(message, 'searchPromise', promise);
				}

				return message.searchPromise;
			},
			openHotel(message, hotel) {
				if (message.searching) return;
				this.$set(message, 'searching', true);

				this.ensureSearch(message).then(({ orderId, storageId, source }) => {
					this.$emit('close-ai');
					this.$router.push({
						name: 'hotels-result',
						params: { code: hotel.code, lang: this.$route.params.lang },
						query: { orderId, storageId, source: JSON.stringify(source) }
					});
				}).catch(() => {
					this.messages.push({ ai: true, error: true, text: this.$t('account.home.ai.error'), time: this.$moment().format('HH:mm') });
					this.scrollDown();
				}).finally(() => this.$set(message, 'searching', false));
			},
			scrollDown() {
				this.$nextTick(() => {
					if (this.$refs.body) this.$refs.body.scrollTop = this.$refs.body.scrollHeight;
				});
			}
		},
		watch: {
			value() {
				if (this.value && !this.loaded) {
					this.loaded = true;
					this.conversationId ? this.loadHistory() : this.greeting();
				}
				this.$nextTick(() => {
					if (this.value) {
						disablePageScroll(this.$refs.body);
						this.scrollDown();
					} else {
						enablePageScroll(this.$refs.body);
					}
				});
			}
		},
		beforeDestroy() {
			enablePageScroll();
		}
	}
</script>

<style lang="scss">
	@import "@/styles/global/variables";
	@import "@/styles/global/mixins";

	.a-ai-chat {
		position: fixed;
		top: 0;
		bottom: 0;
		left: 0;
		z-index: 12;
		width: 420px;
		max-width: 100%;
		display: flex;
		flex-direction: column;
		background-color: $white;
		box-shadow: 0px 0px 30px rgba(86, 86, 86, 0.2);

		&-header {
			display: flex;
			align-items: center;
			flex-shrink: 0;
			padding: 18px 20px;
			background-color: $green-persian;
			color: $white;

			&-avatar {
				display: flex;
				align-items: center;
				justify-content: center;
				flex-shrink: 0;
				width: 40px;
				height: 40px;
				border-radius: 50%;
				background-color: rgba($white, .18);
				margin-right: 12px;
			}
			&-info {
				flex: 1;
				min-width: 0;
			}
			&-title {
				display: block;
				font-size: 16px;
				font-weight: 700;
			}
			&-status {
				display: flex;
				align-items: center;
				font-size: 12px;
				opacity: .9;
				margin-top: 2px;
				i {
					width: 7px;
					height: 7px;
					border-radius: 50%;
					background-color: #A7F3D0;
					margin-right: 6px;
				}
			}
			&-close {
				cursor: pointer;
				padding: 4px;
				opacity: .9;
				transition: opacity $transition;
				&:not(:last-child) {
					margin-right: 8px;
				}
				&:hover {
					opacity: 1;
				}
			}
		}

		&-body {
			flex: 1;
			overflow-y: auto;
			display: flex;
			flex-direction: column;
			padding: 18px 18px 8px;
			background-color: rgba($cyprus, .03);
		}

		&-row {
			display: flex;
			justify-content: flex-end;
			align-items: flex-end;
			flex-shrink: 0;
			margin-bottom: 12px;
			&.is-ai {
				justify-content: flex-start;
			}
		}

		&-avatar {
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			width: 30px;
			height: 30px;
			border-radius: 50%;
			background-color: $green-persian;
			color: $white;
			margin-right: 8px;
		}

		&-bubble {
			max-width: 300px;
			font-size: 14px;
			line-height: 1.5;
			padding: 11px 14px;
			background-color: $green-persian;
			color: $white;
			border-radius: 14px 14px 4px 14px;
			.is-ai & {
				background-color: $white;
				color: $black;
				border: 1px solid rgba($black, .15);
				border-radius: 14px 14px 14px 4px;
			}
			&.is-error {
				border-color: $red;
				color: $red;
			}
			&-text {
				display: block;
				white-space: pre-line;
			}
		}

		&-time {
			display: block;
			font-size: 11px;
			margin-top: 6px;
			text-align: right;
			color: rgba($white, .8);
			.is-ai & {
				color: rgba($black, .4);
			}
		}

		&-card {
			display: flex;
			margin-top: 10px;
			background-color: $white;
			border: 1px solid rgba($black, .15);
			border-radius: $border-radius;
			padding: 10px;
			cursor: pointer;
			transition: border-color $transition, opacity $transition;
			&:hover {
				border-color: $green-persian;
			}
			&.is-loading {
				opacity: .6;
				cursor: progress;
			}
			&-info {
				min-width: 0;
			}
			&-name {
				display: block;
				font-size: 13px;
				font-weight: 700;
				color: $black;
			}
			&-meta {
				display: block;
				font-size: 12px;
				color: rgba($black, .5);
				margin-top: 2px;
			}
			&-reason {
				display: block;
				font-size: 12px;
				color: $black;
				margin-top: 5px;
			}
		}

		&-typing {
			display: flex;
			align-items: center;
			padding: 13px 15px;
			i {
				width: 6px;
				height: 6px;
				border-radius: 50%;
				background-color: $green-persian;
				animation: a-ai-chat-blink 1.2s infinite;
				&:not(:last-child) {
					margin-right: 4px;
				}
				&:nth-child(2) {
					animation-delay: .2s;
				}
				&:nth-child(3) {
					animation-delay: .4s;
				}
			}
		}

		&-footer {
			display: flex;
			align-items: center;
			flex-shrink: 0;
			padding: 14px 18px 18px;
			background-color: rgba($cyprus, .03);
		}

		&-input {
			flex: 1;
			min-width: 0;
			font-size: 14px;
			color: $black;
			background-color: $white;
			border: 1px solid rgba($black, .15);
			border-radius: 26px;
			padding: 14px 16px;
			outline: none;
			transition: border-color $transition;
			&:focus {
				border-color: $green-persian;
			}
			&::placeholder {
				color: rgba($black, .4);
			}
			&:disabled {
				opacity: .5;
			}
		}

		&-send {
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			width: 46px;
			height: 46px;
			border-radius: 50%;
			background-color: $green-persian;
			color: $white;
			margin-left: 10px;
			cursor: pointer;
			transition: background-color $transition, opacity $transition;
			&:hover {
				background-color: $cyprus;
			}
			&.is-disabled {
				opacity: .5;
				pointer-events: none;
			}
			.app-icon svg {
				fill: none;
				stroke: currentColor;
				stroke-width: 2;
			}
		}
	}

	@keyframes a-ai-chat-blink {
		0%, 80%, 100% { opacity: .3; }
		40% { opacity: 1; }
	}
</style>
